import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, ShieldAlert, ArrowRight } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { SEO_PAGES, DISCLAIMER_TEXT } from "@/components/landing/seoPages";

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

export default function LandingPage({ slug }) {
  const page = SEO_PAGES[slug];
  const [heroImg, setHeroImg] = useState(null);

  useEffect(() => {
    if (!page) return;
    document.title = page.title;
    setMeta("description", page.description);
    setMeta("keywords", page.keywords.join(", "));
    setCanonical(`https://www.drohnen-ops.de${page.path}`);
    window.scrollTo(0, 0);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/site-images?slot=seo-${slug}`)
      .then((r) => setHeroImg(r.data[0] || null)).catch(() => {});
  }, [page, slug]);

  if (!page) {
    return (
      <div className="min-h-screen bg-[#09090B] px-6 py-40 text-center text-white">
        <p className="font-tac text-sm text-white/60">Seite nicht gefunden.</p>
        <Link to="/" className="mt-4 inline-block font-tac text-xs tracking-[0.25em] text-[#F5B301]">ZURÜCK ZUR STARTSEITE</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white selection:bg-[#F5B301] selection:text-black">
      <div className="grain" aria-hidden="true" />
      <Navbar />
      <main data-testid={`seo-page-${slug}`}>
        {/* HERO */}
        <section className="relative overflow-hidden border-b border-white/10 px-6 pb-16 pt-36 lg:px-16 lg:pb-24 lg:pt-44">
          <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.06] [background-image:linear-gradient(#F5B301_1px,transparent_1px),linear-gradient(90deg,#F5B301_1px,transparent_1px)] [background-size:44px_44px]" />
          <div className="relative z-10 max-w-4xl">
            <p className="font-tac text-xs tracking-[0.35em] text-[#F5B301]">{page.kicker.toUpperCase()}</p>
            <h1 data-testid={`seo-h1-${slug}`} className="mt-6 font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-6xl">
              {page.h1}
            </h1>
            <p className="mt-7 max-w-2xl font-tac text-sm leading-relaxed text-[#A1A1AA]">{page.intro}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="/#kontakt" data-testid={`seo-cta-${slug}`} className="group inline-flex items-center gap-2 bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black transition-transform duration-300 hover:scale-[1.03]">
                KONTAKT AUFNEHMEN <ArrowUpRight className="h-4 w-4" />
              </a>
              <Link to="/" className="inline-flex items-center gap-2 border border-white/25 px-8 py-4 font-tac text-xs tracking-[0.25em] text-white/90 transition-colors hover:border-white">
                ALLE LEISTUNGEN
              </Link>
            </div>
          </div>
        </section>

        {heroImg && (
          <section className="border-b border-white/10" data-testid={`seo-heroimg-${slug}`}>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${heroImg.url}`} alt={heroImg.alt || page.h1} className="h-[42vh] w-full object-cover lg:h-[56vh]" />
          </section>
        )}

        {/* WAS WIR ERFASSEN */}
        <section className="border-b border-white/10 px-6 py-16 lg:px-16 lg:py-20">
          <h2 className="font-display text-2xl font-black uppercase tracking-tighter sm:text-3xl">Was wir erfassen</h2>
          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {page.capture.map((c, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                className="flex items-start gap-3 border border-white/10 bg-white/[0.02] p-4"
              >
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#F5B301]" />
                <span className="font-tac text-xs leading-relaxed text-white/80">{c}</span>
              </motion.li>
            ))}
          </ul>
        </section>

        {/* SECTIONS */}
        <section className="border-b border-white/10 px-6 py-16 lg:px-16 lg:py-20">
          <div className="grid gap-12 lg:grid-cols-2">
            {page.sections.map((s, i) => (
              <div key={i}>
                <h2 className="font-display text-xl font-black uppercase tracking-tighter sm:text-2xl">{s.h2}</h2>
                <p className="mt-4 max-w-xl font-tac text-sm leading-relaxed text-[#A1A1AA]">{s.text}</p>
              </div>
            ))}
          </div>
          {/* DISCLAIMER */}
          <div className="mt-12 flex max-w-3xl items-start gap-3 border border-[#F5B301]/30 bg-[#F5B301]/[0.04] p-5">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#F5B301]" />
            <p className="font-tac text-xs leading-relaxed text-white/70">{DISCLAIMER_TEXT}</p>
          </div>
        </section>

        {/* RELATED / INTERNE VERLINKUNG */}
        <section className="border-b border-white/10 px-6 py-16 lg:px-16 lg:py-20">
          <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">WEITERE LEISTUNGEN</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {page.related.map((r) => {
              const rp = SEO_PAGES[r];
              if (!rp) return null;
              return (
                <Link
                  key={r}
                  to={rp.path}
                  data-testid={`seo-related-${r}`}
                  className="group flex items-center justify-between gap-3 border border-white/10 bg-white/[0.02] p-5 transition-colors hover:border-[#F5B301]/50"
                >
                  <span className="font-display text-lg font-black uppercase tracking-tight text-white group-hover:text-[#F5B301]">{rp.kicker}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-white/40 transition-colors group-hover:text-[#F5B301]" />
                </Link>
              );
            })}
          </div>
        </section>

        {/* CLOSING */}
        <section className="px-6 py-20 text-center lg:px-16 lg:py-28">
          <h2 className="mx-auto max-w-3xl font-display text-3xl font-black uppercase leading-[0.95] tracking-tighter sm:text-5xl">
            Sie prüfen – <span className="text-[#F5B301]">wir erfassen.</span>
          </h2>
          <a href="/#kontakt" className="mt-10 inline-flex items-center gap-2 bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black transition-transform duration-300 hover:scale-[1.03]">
            KONTAKT AUFNEHMEN <ArrowUpRight className="h-4 w-4" />
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
}
